import React from 'react';
import { extractApiErrorMessage, notifications } from '../../utils';
import { Title, Loader, ClearFilters, DateWeekRangeSelector } from '../../elements';
import {
  Box,
  Button,
  Chip,
  Icon,
  IconButton,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { V4_ICON_BUTTON, V4_TABLE } from '../../theme/v4Compat';
import { DEFAULT_ROWS_PAGE_BIG_OPTIONS, DEFAULT_ROWS_PER_PAGE_MIDDLE, LoaderSize } from '../../constants/constants';
import { useTranslation } from 'react-i18next';
import { HealthSystemDomainDataImportResultSlaughterImportHistoryItemDto } from '../../api/HealthSystemApi';
import { ApiService } from '../../hooks';
import { DateRange } from '../../utils/date-range';
import { PrettyJsonString } from '../../elements';

const SlaughterImportHistory: React.FC<any> = () => {
  const styles = sxStyles(useTheme());
  const { t } = useTranslation();

  const apiService = new ApiService();

  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const [importHistory, setImportHistory] = React.useState<
    HealthSystemDomainDataImportResultSlaughterImportHistoryItemDto[]
  >([]);

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE_MIDDLE);
  const [total, setTotal] = React.useState(0);

  const [isDateRangeReady, setIsDateRangeReady] = React.useState(false);
  const [weekRangeResetKey, setWeekRangeResetKey] = React.useState(0);

  const [selectedDateFrom, setSelectedDateFrom] = React.useState<Date | null>(null);

  const [anchorEl, setAnchorEl] = React.useState<{ [key: string]: HTMLButtonElement | null }>({});

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };

  const loadImportHistory = async () => {
    if (!selectedDateFrom) {
      return;
    }

    setIsBusy(true);

    const searchPage = page === 0 ? 1 : page + 1;

    apiService.api.dataImport
      .dataImportGetSlaughterImportHistory({
        FromDate: selectedDateFrom?.toISOString() ?? '',
        PageIndex: searchPage,
        PageSize: rowsPerPage
      })
      .then((result) => {
        setImportHistory(result.data.items);
        setTotal(result.data.totalCount);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (!loaded && selectedDateFrom) {
      loadImportHistory();
    }
  }, [loaded, selectedDateFrom]);

  const handlePayloadClick = (id: string, event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl({ ...anchorEl, [id]: event.currentTarget });
  };

  const handlePayloadClose = (id: string) => {
    setAnchorEl({ ...anchorEl, [id]: null });
  };

  const renderPayloadPopover = (item: HealthSystemDomainDataImportResultSlaughterImportHistoryItemDto) => {
    const open = Boolean(anchorEl[item.id]);

    return (
      <>
        <Tooltip arrow title={t('f-com-view-payload')}>
          <IconButton color="primary" onClick={(e) => handlePayloadClick(item.id, e)} sx={V4_ICON_BUTTON}>
            <Icon>{'code'}</Icon>
          </IconButton>
        </Tooltip>

        <Popover
          id={`payload-${item.id}`}
          open={open}
          anchorEl={anchorEl[item.id]}
          onClose={() => handlePayloadClose(item.id)}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'center'
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'center'
          }}
        >
          <Box sx={{ margin: 1 }}>
            <PrettyJsonString data={item.payloadJson} />
          </Box>
        </Popover>
      </>
    );
  };

  const clearFilters = () => {
    setPage(0);

    if (defaultDateRangeRef.current) {
      setSelectedDateFrom(defaultDateRangeRef.current.from);
    } else {
      setSelectedDateFrom(null);
    }

    setWeekRangeResetKey((prev) => prev + 1);
    setLoaded(false);
  };

  const defaultDateRangeRef = React.useRef<DateRange | null>(null);

  const onDateRangeInitialized = (defaultRange: DateRange) => {
    defaultDateRangeRef.current = defaultRange;

    if (!isDateRangeReady) {
      setSelectedDateFrom(defaultRange.from);
      setIsDateRangeReady(true);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        {/* title & spinner */}

        <Grid container direction="row" sx={styles.paperHeader}>
          <Grid item container md={6} xs={6} spacing={1}>
            <Grid item>
              <Icon color="primary" fontSize="large">
                {'history'}
              </Icon>
            </Grid>
            <Grid item>
              <Title>{t('f-slaughter-import-history')}</Title>
            </Grid>
            <Grid item>
              <Chip label={total} variant="outlined" />
            </Grid>
          </Grid>

          <Grid item container sx={styles.justifyRight} md={6} xs={6}>
            <Grid item>
              <Loader isLoading={isBusy} size={LoaderSize.SMALL} />
            </Grid>
          </Grid>
        </Grid>

        <Grid container spacing={1}>
          <DateWeekRangeSelector
            key={weekRangeResetKey}
            onInitialized={onDateRangeInitialized}
            onChange={(fromDate) => {
              setSelectedDateFrom(fromDate);
            }}
            fullWidth
            isHourlyFormat
            monthsBack={6}
            allowFuture={false}
            withWeekSelect={false}
          />

          <Grid container justifyContent="flex-end" direction="row" item sx={styles.mt12}>
            <ClearFilters onClick={clearFilters} />

            <Button
              id="searchBtn"
              variant="contained"
              color="primary"
              size="medium"
              sx={styles.withMargin}
              disabled={!loaded}
              onClick={() => setLoaded(false)}
              startIcon={<Icon>{'search'}</Icon>}
            >
              {t('f-search')}
            </Button>
          </Grid>
        </Grid>

        {/* Results */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <TablePagination
              rowsPerPageOptions={DEFAULT_ROWS_PAGE_BIG_OPTIONS}
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              component="div"
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Table size="small" id="slaughterImportHistoryTable" sx={V4_TABLE}>
            <TableHead sx={styles.tableHeader}>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>{t('f-com-imported-at')}</TableCell>
                <TableCell>{t('f-com-animal')}</TableCell>
                <TableCell>{t('f-com-farm')}</TableCell>
                <TableCell>{t('f-com-payload')}</TableCell>
              </TableRow>
            </TableHead>

            {loaded && Array.isArray(importHistory) && importHistory.length > 0 && (
              <>
                <TableBody>
                  {importHistory.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.id}</TableCell>
                      <TableCell>{new Date(row.importedAt).toLocaleString()}</TableCell>
                      <TableCell>{row.animal?.number ?? '-'}</TableCell>
                      <TableCell>{row.farm ? (row.farm.name ?? row.farm.number) : '-'}</TableCell>
                      <TableCell>{renderPayloadPopover(row)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={DEFAULT_ROWS_PAGE_BIG_OPTIONS}
                      count={total}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableRow>
                </TableFooter>
              </>
            )}
          </Table>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SlaughterImportHistory;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    paperHeader: {
      marginBottom: theme.spacing(2)
    },
    justifyRight: {
      justifyContent: 'flex-end',
      alignItems: 'center'
    },
    mt12: {
      marginTop: theme.spacing(1.5)
    },
    withMargin: {
      marginLeft: theme.spacing(1)
    },
    tableHeader: {
      backgroundColor: theme.palette.grey[200]
    }
  }) as const;
